import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  Switch,
  Checkbox,
  Image,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faEdit, faAddressCard, faLocationDot, faImage, faUser } from "@fortawesome/free-solid-svg-icons";
import DataTable from "../AppDataTable";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as IconButton from "../../themes/IconButtonSet";
import { tableCustomStyles, StatusActive } from "../../themes/TableStyles";
import { useDropzone } from "react-dropzone";

const DLDEmployeeManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [officeTypes, setOfficeTypes] = useState([]);
  const [offices, setOffices] = useState([]);
  const [filteredOffices, setFilteredOffices] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOfficeType, setFilterOfficeType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    DLDEmployeeID: "",
    FirstName: "",
    LastName: "",
    FirstName2: "",
    LastName2: "",
    Gender: "M",
    OfficeType: "",
    OfficeID: "",
    DepartmentName: "",
    Position: "",
    Email: "",
    Phone: "",
    Address: "",
    ProfileImage: "",
    Remark: "",
    Status: "1",
    Password: "",
    ConfirmPassword: "",
    RoleID: "",
    UserStatus: "1",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [privacyChecked, setPrivacyChecked] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "รหัสเจ้าหน้าที่",
      selector: (row) => row.DLDEmployeeID,
      sortable: true,
    },
    {
      name: "ชื่อ-นามสกุล",
      selector: (row) => `${row.FirstName} ${row.LastName}`,
      sortable: true,
    },
    {
      name: "หน่วยงาน",
      selector: (row) => {
        const officeType = officeTypes.find((t) => t.OfficeTypeID === row.OfficeType);
        return officeType ? officeType.OfficeTypeName : row.OfficeType || "-";
      },
      sortable: true,
    },
    {
      name: "สถานะ",
      selector: (row) => <StatusActive status={row.Status} />,
      sortable: true,
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.Edit
          icon={faEdit}
          onClick={() => handleEdit(row)}
          label="แก้ไข"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchOfficeTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setOfficeTypes(response.OfficeTypeList || []);
      }
    } catch (error) {
      console.error("Error fetching office types:", error);
    }
  };

  const fetchOffices = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setOffices(response.OfficeList || []);
        setFilteredOffices(response.OfficeList || []);
      }
    } catch (error) {
      console.error("Error fetching offices:", error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getRolesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setRoles(response.RolesList || []);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const fetchEmployees = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDEmployeeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.employeeList || [];
        setEmployees(data);
        setFilteredEmployees(data);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchEmployees();
      fetchOfficeTypes();
      fetchOffices();
      fetchRoles();
    }
  }, [sessionId]);

  useEffect(() => {
    if (formData.OfficeType) {
      const filtered = offices.filter(office => office.OfficeType === formData.OfficeType);
      setFilteredOffices(filtered);
      if (!filtered.some(office => office.OfficeID === formData.OfficeID)) {
        setFormData(prev => ({ ...prev, OfficeID: "" }));
      }
    } else {
      setFilteredOffices(offices);
    }
  }, [formData.OfficeType, offices]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    applyFilters(value, filterOfficeType, filterStatus);
  };

  const handleOfficeTypeFilter = (e) => {
    const value = e.target.value;
    setFilterOfficeType(value);
    applyFilters(searchTerm, value, filterStatus);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    applyFilters(searchTerm, filterOfficeType, value);
  };

  const applyFilters = (search, officeType, status) => {
    let filtered = employees;

    if (search) {
      const searchTerms = search.split(" ").filter((term) => term.length > 0);
      filtered = filtered.filter((row) => {
        const officeTypeName = officeTypes.find((t) => t.OfficeTypeID === row.OfficeType)?.OfficeTypeName || row.OfficeType || "";
        return searchTerms.every((term) =>
          [
            row.DLDEmployeeID.toLowerCase(),
            row.FirstName.toLowerCase(),
            row.LastName.toLowerCase(),
            officeTypeName.toLowerCase(),
            row.Status ? "active" : "inactive",
          ].some((field) => field.includes(term))
        );
      });
    }

    if (officeType) {
      filtered = filtered.filter((row) => row.OfficeType === officeType);
    }

    if (status) {
      filtered = filtered.filter((row) => (status === "1" ? row.Status : !row.Status));
    }

    setFilteredEmployees(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilterOfficeType("");
    setFilterStatus("");
    setFilteredEmployees(employees);
  };

  const handleAddNew = () => {
    setFormData({
      DLDEmployeeID: "",
      FirstName: "",
      LastName: "",
      FirstName2: "",
      LastName2: "",
      Gender: "M",
      OfficeType: "",
      OfficeID: "",
      DepartmentName: "",
      Position: "",
      Email: "",
      Phone: "",
      Address: "",
      ProfileImage: "",
      Remark: "",
      Status: "1",
      Password: "",
      ConfirmPassword: "",
      RoleID: "",
      UserStatus: "1",
    });
    setIsEditing(false);
    setShowForm(true);
    setErrors({});
    setPrivacyChecked(false);
  };

  const handleEdit = async (row) => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDEmployeeByID",
        data: { DLDEmployeeID: row.DLDEmployeeID },
        sessionId: sessionId || "",
      });

      if (response.result === "success" && response.employee) {
        const employeeData = response.employee;

        // Fetch user data for RoleID and UserStatus
        const userResponse = await apiService({
          method: "POST",
          url: "/getUserByProfileID",
          data: { ProfileID: row.DLDEmployeeID, ProfileType: "Employee" },
          sessionId: sessionId || "",
        });

        const userData = userResponse.result === "success" && userResponse.user ? userResponse.user : {};

        setFormData({
          ...employeeData,
          Status: employeeData.Status ? "1" : "0",
          Password: "",
          ConfirmPassword: "",
          RoleID: userData.RoleID || "",
          UserStatus: userData.Status ? "1" : "0",
        });
        setIsEditing(true);
        setShowForm(true);
        setErrors({});
        setPrivacyChecked(true);
      } else {
        alert("ไม่พบข้อมูลเจ้าหน้าที่");
      }
    } catch (error) {
      console.error("Error fetching employee data:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูลเจ้าหน้าที่");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleStatusChange = (e) => {
    setFormData({ ...formData, Status: e.target.checked ? "1" : "0" });
  };

  const handleUserStatusChange = (e) => {
    setFormData({ ...formData, UserStatus: e.target.checked ? "1" : "0" });
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*",
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData({ ...formData, ProfileImage: reader.result });
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "DLDEmployeeID":
        if (!value) {
          newErrors[name] = "กรุณากรอกรหัสเจ้าหน้าที่";
        } else if (value.length > 30) {
          newErrors[name] = "รหัสเจ้าหน้าที่ต้องไม่เกิน 30 ตัวอักษร";
        } else {
          delete newErrors[name];
        }
        break;
      case "FirstName":
      case "LastName":
      case "OfficeType":
      case "OfficeID":
      case "DepartmentName":
      case "Position":
      case "Phone":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      case "Email":
        if (!value) {
          newErrors[name] = "กรุณากรอกอีเมล";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors[name] = "รูปแบบอีเมลไม่ถูกต้อง";
        } else {
          delete newErrors[name];
        }
        break;
      case "Password":
        if (!isEditing && !value) {
          newErrors[name] = "กรุณากรอกรหัสผ่าน";
        } else if (value && value.length < 6) {
          newErrors[name] = "รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร";
        } else {
          delete newErrors[name];
        }
        break;
      case "ConfirmPassword":
        if (!isEditing && !value) {
          newErrors[name] = "กรุณายืนยันรหัสผ่าน";
        } else if (value && value !== formData.Password) {
          newErrors[name] = "รหัสผ่านไม่ตรงกัน";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        delete newErrors[name];
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.DLDEmployeeID &&
      formData.DLDEmployeeID.length <= 30 &&
      formData.FirstName &&
      formData.LastName &&
      formData.OfficeType &&
      formData.OfficeID &&
      formData.DepartmentName &&
      formData.Position &&
      formData.Email &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email) &&
      formData.Phone &&
      (!isEditing || (formData.Password ? formData.Password.length >= 6 : true)) &&
      (!isEditing || (formData.Password ? formData.ConfirmPassword === formData.Password : true)) &&
      (!isEditing || (formData.ConfirmPassword ? formData.ConfirmPassword === formData.Password : true)) &&
      Object.keys(errors).length === 0 &&
      privacyChecked
    );
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const apiUrl = isEditing ? "/updateDLDEmployee" : "/registerDLDEmployee";
      const submitData = {
        ...formData,
        Status: formData.Status === "1",
        // ไม่ต้องส่ง CreateBy และ UpdatedBy เพราะ Backend จะดึงจาก x-session-id header
        ProfileImage: formData.ProfileImage || "",
      };

      const response = await apiService({
        method: "POST",
        url: apiUrl,
        data: submitData,
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        // Backend registerDLDEmployee และ updateDLDEmployee ได้จัดการ User table ให้แล้ว
        // ไม่จำเป็นต้องเรียก API เพิ่มเติม
        alert(isEditing ? "อัปเดตข้อมูลเจ้าหน้าที่สำเร็จ!" : "สร้างข้อมูลเจ้าหน้าที่สำเร็จ!");
        setShowForm(false);
        fetchEmployees();
      }
    } catch (error) {
      console.error("Error saving employee:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      DLDEmployeeID: "",
      FirstName: "",
      LastName: "",
      FirstName2: "",
      LastName2: "",
      Gender: "M",
      OfficeType: "",
      OfficeID: "",
      DepartmentName: "",
      Position: "",
      Email: "",
      Phone: "",
      Address: "",
      ProfileImage: "",
      Remark: "",
      Status: "1",
      Password: "",
      ConfirmPassword: "",
      RoleID: "",
      UserStatus: "1",
    });
    setErrors({});
    setPrivacyChecked(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบเจ้าหน้าที่นี้?")) return;
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateDLDEmployee",
        data: {
          ...formData,
          Status: false,
          UpdatedBy: sessionId,
        },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        // Update user status to inactive
        await apiService({
          method: "POST",
          url: "/updateUserByProfileID",
          data: {
            ProfileID: formData.DLDEmployeeID,
            ProfileType: "Employee",
            Status: false,
            UpdatedBy: sessionId,
          },
          sessionId: sessionId || "",
        });

        alert("ลบเจ้าหน้าที่สำเร็จ!");
        setShowForm(false);
        fetchEmployees();
      }
    } catch (error) {
      console.error("Error deleting employee:", error);
      alert("เกิดข้อผิดพลาดในการลบ: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              รายการข้อมูลผู้ใช้งาน (เจ้าหน้าที่)
            </Heading>
          </HStack>
        </Box>

        {/* ส่วนฟอร์ม */}
        {showForm && (
          <Box
            p={5}
            borderWidth={1}
            borderRadius="lg"
            boxShadow="lg"
            w="100%"
            bg="white"
          >
            <Heading size="md" mb={4}>
              {isEditing ? "แก้ไขข้อมูลเจ้าหน้าที่" : "เพิ่มข้อมูลเจ้าหน้าที่"}
            </Heading>

            {/* ข้อมูลทั่วไป */}
            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
              <Box mb={5} borderRadius="full" w="100%" bgColor="iTeal.500" color="white" p={2}>
                <HStack spacing={5} ml={5}>
                  <FontAwesomeIcon icon={faAddressCard} color="white" size="xl" />
                  <Heading size="lg" fontWeight="normal">ข้อมูลทั่วไป</Heading>
                </HStack>
              </Box>
              <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.DLDEmployeeID}>
                    <FormLabel>รหัสเจ้าหน้าที่</FormLabel>
                    <Input
                      name="DLDEmployeeID"
                      value={formData.DLDEmployeeID}
                      onChange={handleInputChange}
                      isDisabled={isEditing}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.DLDEmployeeID}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.FirstName}>
                    <FormLabel>ชื่อ</FormLabel>
                    <Input
                      name="FirstName"
                      value={formData.FirstName}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.FirstName}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.LastName}>
                    <FormLabel>นามสกุล</FormLabel>
                    <Input
                      name="LastName"
                      value={formData.LastName}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.LastName}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>ชื่อ (ภาษาอื่น)</FormLabel>
                    <Input
                      name="FirstName2"
                      value={formData.FirstName2}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>นามสกุล (ภาษาอื่น)</FormLabel>
                    <Input
                      name="LastName2"
                      value={formData.LastName2}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>เพศ</FormLabel>
                    <Select
                      name="Gender"
                      value={formData.Gender}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    >
                      <option value="M">ชาย</option>
                      <option value="F">หญิง</option>
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.Position}>
                    <FormLabel>ตำแหน่ง</FormLabel>
                    <Input
                      name="Position"
                      value={formData.Position}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.Position}</FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            {/* ข้อมูลหน่วยงาน */}
            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
              <Box mb={5} borderRadius="full" w="100%" bgColor="iBlue.500" color="white" p={2}>
                <HStack spacing={5} ml={5}>
                  <FontAwesomeIcon icon={faLocationDot} color="white" size="xl" />
                  <Heading size="lg" fontWeight="normal">ข้อมูลหน่วยงาน</Heading>
                </HStack>
              </Box>
              <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.OfficeType}>
                    <FormLabel>ประเภทสำนักงาน</FormLabel>
                    <Select
                      name="OfficeType"
                      value={formData.OfficeType}
                      onChange={handleInputChange}
                      placeholder="เลือกประเภทสำนักงาน"
                      colorScheme="iBlue"
                    >
                      {officeTypes.map((type) => (
                        <option key={type.OfficeTypeID} value={type.OfficeTypeID}>
                          {type.OfficeTypeName}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.OfficeType}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.OfficeID}>
                    <FormLabel>สำนักงาน</FormLabel>
                    <Select
                      name="OfficeID"
                      value={formData.OfficeID}
                      onChange={handleInputChange}
                      placeholder="เลือกสำนักงาน"
                      colorScheme="iBlue"
                      isDisabled={!formData.OfficeType}
                    >
                      {filteredOffices.map((office) => (
                        <option key={office.OfficeID} value={office.OfficeID}>
                          {office.OfficeName}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.OfficeID}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.DepartmentName}>
                    <FormLabel>หน่วยงาน</FormLabel>
                    <Input
                      name="DepartmentName"
                      value={formData.DepartmentName}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                    <FormErrorMessage>{errors.DepartmentName}</FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            {/* ข้อมูลการติดต่อ */}
            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
              <Box mb={5} borderRadius="full" w="100%" bgColor="iBlue.500" color="white" p={2}>
                <HStack spacing={5} ml={5}>
                  <FontAwesomeIcon icon={faLocationDot} color="white" size="xl" />
                  <Heading size="lg" fontWeight="normal">ข้อมูลการติดต่อ</Heading>
                </HStack>
              </Box>
              <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.Email}>
                    <FormLabel>อีเมล</FormLabel>
                    <Input
                      name="Email"
                      value={formData.Email}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                    <FormErrorMessage>{errors.Email}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.Phone}>
                    <FormLabel>เบอร์โทรศัพท์</FormLabel>
                    <Input
                      name="Phone"
                      value={formData.Phone}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                    <FormErrorMessage>{errors.Phone}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem colSpan={3}>
                  <FormControl>
                    <FormLabel>ที่อยู่</FormLabel>
                    <Input
                      name="Address"
                      value={formData.Address}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={3}>
                  <FormControl>
                    <FormLabel>หมายเหตุ</FormLabel>
                    <Textarea
                      name="Remark"
                      value={formData.Remark}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            {/* รูปโปรไฟล์ */}
            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
              <Box mb={5} borderRadius="full" w="100%" bgColor="iGreen.500" color="white" p={2}>
                <HStack spacing={5} ml={5}>
                  <FontAwesomeIcon icon={faImage} color="white" size="xl" />
                  <Heading size="lg" fontWeight="normal">รูปโปรไฟล์</Heading>
                </HStack>
              </Box>
              <Box {...getRootProps()} borderWidth={2} borderStyle="dashed" borderRadius="md" w="70%" p={5} textAlign="center" cursor="pointer">
                <Input {...getInputProps()} colorScheme="iGreen" />
                {formData.ProfileImage ? (
                  <Image src={formData.ProfileImage} alt="Profile Preview" maxH="200px" mx="auto" />
                ) : (
                  <Text h={20}>ลากและวางรูปภาพที่นี่ หรือคลิกเพื่อเลือกไฟล์</Text>
                )}
              </Box>
            </Box>

            {/* ชื่อผู้ใช้และรหัสผ่าน */}
            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
              <Box mb={5} borderRadius="full" w="100%" bgColor="iPurple.500" color="white" p={2}>
                <HStack spacing={5} ml={5}>
                  <FontAwesomeIcon icon={faUser} color="white" size="xl" />
                  <Heading size="lg" fontWeight="normal">ชื่อผู้ใช้และรหัสผ่าน</Heading>
                </HStack>
              </Box>
              <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.Email}>
                    <FormLabel>ชื่อผู้ใช้ (อีเมล)</FormLabel>
                    <Input
                      name="Email"
                      value={formData.Email}
                      onChange={handleInputChange}
                      isDisabled
                      colorScheme="iPurple"
                    />
                    <FormErrorMessage>{errors.Email}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired={!isEditing} isInvalid={!!errors.Password}>
                    <FormLabel>รหัสผ่าน</FormLabel>
                    <Input
                      name="Password"
                      type="password"
                      value={formData.Password}
                      onChange={handleInputChange}
                      colorScheme="iPurple"
                    />
                    <FormErrorMessage>{errors.Password}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired={!isEditing} isInvalid={!!errors.ConfirmPassword}>
                    <FormLabel>ยืนยันรหัสผ่าน</FormLabel>
                    <Input
                      name="ConfirmPassword"
                      type="password"
                      value={formData.ConfirmPassword}
                      onChange={handleInputChange}
                      colorScheme="iPurple"
                    />
                    <FormErrorMessage>{errors.ConfirmPassword}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>สิทธิ์การใช้งาน</FormLabel>
                    <Select
                      name="RoleID"
                      value={formData.RoleID}
                      onChange={handleInputChange}
                      placeholder="เลือกสิทธิ์การใช้งาน"
                      colorScheme="iPurple"
                    >
                      {roles.map((role) => (
                        <option key={role.RoleID} value={role.RoleID}>
                          {role.RoleName}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>สถานะผู้ใช้</FormLabel>
                    <Switch
                      name="UserStatus"
                      isChecked={formData.UserStatus === "1"}
                      onChange={handleUserStatusChange}
                      colorScheme="iPurple"
                    />
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            {/* นโยบายความเป็นส่วนตัว */}
            <Box p={5}>
              <Checkbox
                isChecked={privacyChecked}
                onChange={(e) => setPrivacyChecked(e.target.checked)}
                size="lg"
                colorScheme="iOrange"
              >
                ฉันได้อ่านนโยบายความเป็นส่วนตัวและเข้าใจนโยบายความเป็นส่วนตัวของเราแล้ว
                และยอมรับเงื่อนไขการใช้งาน รวมถึงนโยบายความเป็นส่วนตัวของเรา
                และยอมรับการใช้งานของคุกกี้ และเทคโนโลยีต่างๆ ที่เกี่ยวข้อง
              </Checkbox>
            </Box>

            {/* ปุ่มควบคุม */}
            <HStack mt={6} justify="flex-end">
              <IconButton.Close onClick={handleCancel} label="ปิด" />
              {isEditing && (
                <IconButton.Delete onClick={handleDelete} label="ลบ" />
              )}
              <IconButton.Save onClick={handleSave} isLoading={isLoading} />
            </HStack>
          </Box>
        )}

        {/* ส่วนตัวกรอง */}
        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
          spacing={4}
        >
          <HStack spacing={4} flexWrap={{ base: "wrap", md: "nowrap" }}>
            <InputGroup w={{ base: "100%", md: "300px" }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาเจ้าหน้าที่..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
              />
            </InputGroup>
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filterOfficeType}
              onChange={handleOfficeTypeFilter}
              placeholder="เลือกประเภทสำนักงาน"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              {officeTypes.map((type) => (
                <option key={type.OfficeTypeID} value={type.OfficeTypeID}>
                  {type.OfficeTypeName}
                </option>
              ))}
            </Select>
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filterStatus}
              onChange={handleStatusFilter}
              placeholder="เลือกสถานะ"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Select>
            <IconButton.Clear onClick={handleClearSearch} label="ล้าง" />
          </HStack>
          <IconButton.Add onClick={handleAddNew} label="เพิ่มข้อมูลใหม่" />
        </HStack>

        {/* ส่วนลิสต์ */}
        {isLoading ? (
          <Spinner size="md" />
        ) : employees.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลเจ้าหน้าที่
          </Text>
        ) : filteredEmployees.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredEmployees}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default DLDEmployeeManagement;